import { useState, useEffect, useRef } from "react";
import { EntryModel } from "lib/models";
import { updateWorkspaceEntryRef } from "lib/api";
import EntryContainer from "./EntryContainer";
import ContextMenu from "./ContextMenu";

interface MoveableEntryProps {
  parentRef: HTMLDivElement | null;
  workspaceUuid: string;
  entryRefId: string;
  entry: EntryModel;
  pos: { x: number; y: number; zIndex: number; width: number; height: number };
  offset: { x: number; y: number };
  zoom: number;
  onDragStart: (element: HTMLDivElement | null) => void;
  onArchiveEntry: (refId: string, entryUuid: string) => Promise<void>;
}

interface MenuPosition {
  x: number;
  y: number;
}

function MoveableEntry({
  parentRef,
  workspaceUuid,
  entryRefId,
  entry,
  pos,
  offset,
  zoom,
  onDragStart,
  onArchiveEntry,
}: MoveableEntryProps) {
  const [isDragStart, setIsDragStart] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState<MenuPosition | null>(null);
  const [position, setPosition] = useState({ x: pos.x, y: pos.y });
  const [selectedRefId, setSelectedRefId] = useState<string | null>(null);
  const [size, setSize] = useState({ width: pos.width, height: pos.height });
  const [isEntryContainerFocused, setIsEntryContainerFocused] = useState(false);

  const dragStartRef = useRef({ x: 0, y: 0 });
  const elementRef = useRef<HTMLDivElement | null>(null);
  const positionRef = useRef(position);
  const sizeRef = useRef({ width: pos.width, height: pos.height });

  const saveEntryRefPosition = async () => {
    await updateWorkspaceEntryRef(
      workspaceUuid,
      entryRefId,
      positionRef.current.x,
      positionRef.current.y,
      pos.zIndex,
      sizeRef.current.width,
      sizeRef.current.height,
    );
  };

  const handleOnResizeChange = (size: { width: number; height: number }) => {
    setSize(size);
    sizeRef.current = size;
  };

  const handleMouseDown = (e: any) => {
    if (e.button == 0) {
      setIsDragStart(true);
      dragStartRef.current = {
        x: e.clientX,
        y: e.clientY,
      };
      onDragStart(elementRef.current);
    }
  };

  const handleMouseMove = (e: any) => {
    if (isDragStart) {
      setIsDragging(true);
      e.preventDefault();
      const dx = (e.clientX - dragStartRef.current.x) / zoom;
      const dy = (e.clientY - dragStartRef.current.y) / zoom;
      const newX = Math.round(position.x + dx);
      const newY = Math.round(position.y + dy);
      setPosition({ x: newX, y: newY });
      positionRef.current = { x: newX, y: newY };
    }
  };

  const handleMouseUp = async () => {
    setIsDragStart(false);
    setIsDragging(false);
    if (
      position.x != positionRef.current.x ||
      position.y != positionRef.current.y
    ) {
      await saveEntryRefPosition();
    }
  };

  useEffect(() => {
    if (isDragStart) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragStart, isMenuOpen]);

  if (!entry || !parentRef) {
    return <></>;
  }

  const centerX = parentRef.clientWidth / 2;
  const centerY = parentRef.clientHeight / 2;
  const posX = position.x + offset.x;
  const posY = position.y + offset.y;
  const finalX = Math.round((posX - centerX) * zoom + centerX);
  const finalY = Math.round((posY - centerY) * zoom + centerY);

  return (
    <>
      <div
        ref={elementRef}
        className="relative w-fit h-fit"
        style={{
          cursor: isDragging
            ? "grabbing"
            : isEntryContainerFocused
              ? "auto"
              : "grab",
          transform: `translate(${finalX}px, ${finalY}px) scale(${zoom})`,
          transformOrigin: "top left",
          userSelect: "none",
          zIndex: `${pos.zIndex}`,
        }}
        onMouseDown={handleMouseDown}
        onDoubleClick={(e) => e.stopPropagation()}
      >
        <EntryContainer
          entryRefId={entryRefId}
          entry={entry}
          isDragging={isDragging}
          isContextMenuOpen={isMenuOpen}
          onContextMenuOpen={(entryRefId: string, pos: MenuPosition | null) => {
            if (pos) {
              setIsMenuOpen(true);
              setMenuPosition(pos);
              setSelectedRefId(entryRefId);
            } else {
              setIsMenuOpen(false);
              setMenuPosition(null);
              setSelectedRefId(null);
            }
          }}
          onFocusChange={setIsEntryContainerFocused}
          size={size}
          onResizeChange={handleOnResizeChange}
          onResizeEnd={saveEntryRefPosition}
        />
      </div>
      <ContextMenu
        isOpen={isMenuOpen}
        position={menuPosition}
        onClose={() => setIsMenuOpen(false)}
        onArchiveEntry={async () => {
          await onArchiveEntry(entryRefId, entry.uuid);
          setIsMenuOpen(false);
        }}
        entryRefId={selectedRefId}
      />
    </>
  );
}

export default MoveableEntry;
